// CreateStoreModal.tsx
import CSVDownload from "components/CSVDownload";
import Config from "config";
import { City, Country, ICity, ICountry, IState, State as StateData } from "country-state-city";
import { Dispatch } from "interfaces/dispatch";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { State } from "state";
import { createAnalyticsEvent, EventTypes, Status } from "state/analytics";
import { FileUploadState, resetUpload, uploadFile } from "state/create_file_upload";
import { deleteProject, fetchProject, updateProject } from "state/project";
import { buildExportFilename, getTemplateHeaders } from "utils/helpers";
import Step2FileUpload from "./Step2FileUpload";

interface Store {
  retailStoreNumber: string;
  storeName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  clusterStoreGroup: string;
  adZone: string;
  abInBevWholesaler: string;
  // molsonCoorsWholesaler: string;
  // constellationWholesaler: string;
  millerWholesaler: string; // was: molsonCoorsWholesaler
  coorsWholesaler: string; // was: constellationWholesaler
  thirdPartyWholesaler: string;
  catalystId: string;
}

interface CreateStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (stores: Store[]) => void;
  projectId: string;
}

type CreateStoreModalCombineProps = RouteComponentProps & PropsFromRedux & CreateStoreModalProps;

// Required fields list - easy to maintain
const REQUIRED_FIELDS: (keyof Store)[] = [
  "retailStoreNumber",
  "storeName",
  "addressLine1",
  "city",
  "state",
  "zipCode",
];

// Searchable Dropdown Component for Location (Country, State, City)
interface LocationDropdownProps {
  label: string;
  value: string;
  options: Array<{ value: string; label: string; code?: string }>;
  onChange: (value: string, code?: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = "Select",
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div style={{ position: "relative" }}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.required}>*</span>}
      </label>
      <div
        style={{
          ...styles.select,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={{ flex: 1, color: "#000" }}>{selectedOption?.label || placeholder}</span>
        {/* <span>▼</span> */}
      </div>
      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "none",
              borderBottom: "1px solid #e5e7eb",
              outline: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: option.value === value ? "#f3f4f6" : "white",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    option.value === value ? "#f3f4f6" : "white";
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.value, option.code);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
              >
                {option.label}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div style={{ padding: "8px 12px", color: "#9ca3af" }}>No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CreateStoreModal: React.FC<CreateStoreModalCombineProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  project,
  updateProject,
  createAnalyticsEvent,
  uploadFile: uploadFileAction,
  fetchProject: fetchProjectAction,
  // updateNotifications: updateNotificationsAction,
  resetUpload,
}) => {
  const [stores, setStores] = useState<Store[]>([
    {
      retailStoreNumber: "",
      storeName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      clusterStoreGroup: "",
      adZone: "",
      abInBevWholesaler: "",
      millerWholesaler: "",
      coorsWholesaler: "",
      thirdPartyWholesaler: "",
      catalystId: "",
    },
  ]);

  const [uploadHover, setUploadHover] = useState(false);
  const [addAnotherHover, setAddAnotherHover] = useState(false);
  const [submitHover, setSubmitHover] = useState(false);
  const [closeHover, setCloseHover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  let label = "Upload New Stores";

  useEffect(() => {
    if (isOpen) {
      resetUpload("STORES");
    }
  }, [isOpen, resetUpload]);

  // Get all countries
  const countries = useMemo(() => {
    return Country.getAllCountries().map((country: ICountry) => ({
      value: country.name,
      label: country.name,
      code: country.isoCode,
    }));
  }, []);

  // Get states based on selected country
  const getStatesForCountry = (countryCode: string) => {
    if (!countryCode) return [];
    return StateData.getStatesOfCountry(countryCode).map((state: IState) => ({
      value: state.name,
      label: state.name,
      code: state.isoCode,
    }));
  };

  // Get cities based on selected country and state
  const getCitiesForState = (countryCode: string, stateCode: string) => {
    if (!countryCode || !stateCode) return [];
    return City.getCitiesOfState(countryCode, stateCode).map((city: ICity) => ({
      value: city.name,
      label: city.name,
    }));
  };

  if (!isOpen) return null;

  const handleInputChange = (index: number, field: keyof Store, value: string, code?: string) => {
    const updatedStores = [...stores];
    updatedStores[index][field] = value;

    // Store country/state codes separately for CSV generation
    if (field === "country" && code) {
      (updatedStores[index] as any).countryCode = code;
      // Reset state and city when country changes
      updatedStores[index].state = "";
      updatedStores[index].city = "";
      (updatedStores[index] as any).stateCode = "";
    }

    if (field === "state" && code) {
      (updatedStores[index] as any).stateCode = code;
      // Reset city when state changes
      updatedStores[index].city = "";
    }

    setStores(updatedStores);
  };

  const handleAddStore = () => {
    setStores([
      ...stores,
      {
        retailStoreNumber: "",
        storeName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        zipCode: "",
        clusterStoreGroup: "",
        adZone: "",
        abInBevWholesaler: "",
        // molsonCoorsWholesaler: "",
        // constellationWholesaler: "",
        millerWholesaler: "", // was: molsonCoorsWholesaler
        coorsWholesaler: "", // was: constellationWholesaler
        thirdPartyWholesaler: "",
        catalystId: "",
      },
    ]);
  };

  // Function to convert stores data to CSV string
  const convertToCSV = (data: Store[]): string => {
    const headers = [
      "Chain Name",
      "Retail Store Number",
      "Store Name",
      "Address Line 1",
      "Address Line 2",
      "City",
      "State",
      "Country",
      "Zip Code",
      "Cluster Store Group",
      "Ad Zone",
      "ABInBev Wholesaler",
      // "Molson Coors Wholesaler",
      // "Constellation Wholesaler",
      // "Miller Wholesaler", // was: "Molson Coors Wholesaler"
      // "Coors Wholesaler", // was: "Constellation Wholesaler"
      "Molson Coors Wholesaler",
      "Constellation Wholesaler",
      "Third Party Wholesaler",
      "Catalyst ID",
    ];

    const csvRows = [headers.join(",")];

    data.forEach((store) => {
      const storeWithCodes = store as any;
      const row = [
        project?.chain?.name || "", // Chain Name from project
        store.retailStoreNumber,
        store.storeName,
        store.addressLine1,
        store.addressLine2,
        store.city, // Full city name
        storeWithCodes.stateCode || store.state, // 2-char state code (e.g., "OK")
        storeWithCodes.countryCode || store.country, // 2-char country code (e.g., "US")
        store.zipCode,
        store.clusterStoreGroup,
        store.adZone,
        store.abInBevWholesaler,
        // store.molsonCoorsWholesaler,
        // store.constellationWholesaler,
        store.millerWholesaler, // was: store.molsonCoorsWholesaler
        store.coorsWholesaler, // was: store.constellationWholesaler
        store.thirdPartyWholesaler,
        store.catalystId,
      ];

      // Escape fields that contain commas or quotes
      const escapedRow = row.map((field) => {
        const fieldStr = String(field);
        if (fieldStr.includes(",") || fieldStr.includes('"') || fieldStr.includes("\n")) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      });

      csvRows.push(escapedRow.join(","));
    });

    return csvRows.join("\n");
  };

  // Function to create a File from CSV string
  const createCSVFile = (csvContent: string, filename: string): File => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    return new File([blob], filename, { type: "text/csv" });
  };

  // Validate required fields
  const validateStores = (): boolean => {
    for (let i = 0; i < stores.length; i++) {
      const store = stores[i];
      const missingFields: string[] = [];

      REQUIRED_FIELDS.forEach((field) => {
        if (!store[field]) {
          missingFields.push(field);
        }
      });

      if (missingFields.length > 0) {
        alert(
          `Store ${i + 1}: Please fill in all required fields:\n${missingFields
            .map((f) => f.replace(/([A-Z])/g, " $1").trim())
            .join(", ")}`
        );
        return false;
      }
    }
    return true;
  };

  // Upload CSV to API using uploadFile action - FOLLOWING FileUpload component pattern
  const uploadCSVToAPI = async (file: File) => {
    const formData = new FormData();

    // Append file (same as FileUpload component does)
    formData.append("file", file);

    // Append projectId (same as FileUpload component does)
    if (projectId) {
      formData.append("projectId", `${projectId}`);
    }

    const offset = moment().utcOffset();
    const timezone = moment.tz.guess();
    const options = { offset, timezone };

    try {
      // Call uploadFile exactly like FileUpload component does
      await uploadFileAction(
        "STORES",
        `${Config.API_SERVICE_URL}/api/stores`,
        options
      )({
        data: formData,
      });

      setStores([
        {
          retailStoreNumber: "",
          storeName: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          country: "",
          zipCode: "",
          clusterStoreGroup: "",
          adZone: "",
          abInBevWholesaler: "",
          millerWholesaler: "",
          coorsWholesaler: "",
          thirdPartyWholesaler: "",
          catalystId: "",
        },
      ]);

      return true;
    } catch (error) {
      console.error("Error uploading CSV:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Validate stores
    if (!validateStores()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const csvContent = convertToCSV(stores);

      const filename = `manual_stores_${new Date().getTime()}.csv`;
      const csvFile = createCSVFile(csvContent, filename);

      await uploadCSVToAPI(csvFile);

      if (fetchProjectAction) {
        await fetchProjectAction(projectId);
      }

      if (updateProject) {
        await updateProject(projectId, {});
      }

      handleAfterUpload("New Stores");

      onSubmit(stores);

      // if (updateNotificationsAction) {
      //   updateNotificationsAction({
      //     type: "success",
      //     message: "Stores created successfully!",
      //   });
      // }

      resetUpload("STORES");
      onClose();
    } catch (error) {
      console.error("Error submitting stores:", error);

      if (project && createAnalyticsEvent) {
        createAnalyticsEvent({
          event: EventTypes.CREATE_NEW_PROJECT_UPLOAD,
          eventType: "Action",
          ...(project.name &&
            project.chain &&
            project.chain.name && {
              projectName: `${project.chain.name} - ${project.name}`,
            }),
          projectType: project.project_type,
          fileUploadType: "New Stores",
          fileUploadStatus: Status.FAILURE,
        });
      }

      // if (updateNotificationsAction) {
      //   updateNotificationsAction({
      //     type: "error",
      //     message: "Failed to create stores. Please try again.",
      //   });
      // }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAfterUpload = (type: "New Stores") => {
    if (project && createAnalyticsEvent) {
      createAnalyticsEvent({
        event: EventTypes.CREATE_NEW_PROJECT_UPLOAD,
        eventType: "Action",
        ...(project.name &&
          project.chain &&
          project.chain.name && {
            projectName: `${project.chain.name} - ${project.name}`,
          }),
        projectType: project.project_type,
        fileUploadType: type,
        fileUploadStatus: Status.SUCCESS,
      });
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>

      <div style={styles.overlay}>
        <div
          style={{
            ...styles.container,
            animation: "modalSlideIn 0.3s ease-out",
          }}
        >
          <div style={styles.header}>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <h2 style={styles.title}>Create Stores Via Template</h2>
                <button
                  style={{
                    ...styles.closeButton,
                  }}
                  onClick={() => { resetUpload("STORES"); onClose(); }}
                  onMouseEnter={() => setCloseHover(true)}
                  onMouseLeave={() => setCloseHover(false)}
                  disabled={isSubmitting}
                >
                  ✕
                </button>
              </div>
              <div style={{ marginBottom: 0 }}>
                <CSVDownload
                  data={[]}
                  headers={getTemplateHeaders(label)}
                  filename={buildExportFilename("csv", `${label}-template`)}
                >
                  <div className="file-upload-header">Download CSV Template</div>
                </CSVDownload>
              </div>
            </div>
            <Step2FileUpload
              prefix={"STORES"}
              url={`${Config.API_SERVICE_URL}/api/stores`}
              label={""}
              dataQa={"stores-file-upload"}
              projectId={projectId}
              afterUploadFunction={() => {
                // Used to update the updated_at field of the projects database table
                updateProject(projectId, {});

                // handle analytics
                handleAfterUpload("New Stores");
              }}
            />
          </div>

          <div style={styles.content}>
            <h3 style={styles.sectionTitle}>Manually Create Stores</h3>

            {stores.map((store, index) => {
              const storeWithCodes = store as any;
              const countryCode = storeWithCodes.countryCode || "";
              const stateCode = storeWithCodes.stateCode || "";

              return (
                <div key={index}>
                  {index > 0 && (
                    <div
                      style={{
                        borderTop: "1px solid #e5e7eb",
                        marginTop: "32px",
                        paddingTop: "32px",
                      }}
                    />
                  )}
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Retail Store Number
                        <span style={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Store Number"
                        style={styles.input}
                        value={store.retailStoreNumber}
                        onChange={(e) =>
                          handleInputChange(index, "retailStoreNumber", e.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Store Name<span style={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Store Name"
                        style={styles.input}
                        value={store.storeName}
                        onChange={(e) => handleInputChange(index, "storeName", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Address Line 1<span style={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Content"
                        style={styles.input}
                        value={store.addressLine1}
                        onChange={(e) => handleInputChange(index, "addressLine1", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Address Line 2</label>
                      <input
                        type="text"
                        placeholder="Content"
                        style={styles.input}
                        value={store.addressLine2}
                        onChange={(e) => handleInputChange(index, "addressLine2", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <LocationDropdown
                        label="Country"
                        value={store.country}
                        options={countries}
                        onChange={(value, code) => handleInputChange(index, "country", value, code)}
                        placeholder="Select Country"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <LocationDropdown
                        label="State"
                        value={store.state}
                        options={getStatesForCountry(countryCode)}
                        onChange={(value, code) => handleInputChange(index, "state", value, code)}
                        placeholder="Select State"
                        required={true}
                        disabled={isSubmitting || !countryCode}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <LocationDropdown
                        label="City"
                        value={store.city}
                        options={getCitiesForState(countryCode, stateCode)}
                        onChange={(value) => handleInputChange(index, "city", value)}
                        placeholder="Select City"
                        required={true}
                        disabled={isSubmitting || !stateCode}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        Zip Code<span style={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Content"
                        style={styles.input}
                        value={store.zipCode}
                        onChange={(e) => handleInputChange(index, "zipCode", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Cluster Store Group</label>
                      <input
                        type="text"
                        placeholder="-"
                        style={styles.input}
                        value={store.clusterStoreGroup}
                        onChange={(e) =>
                          handleInputChange(index, "clusterStoreGroup", e.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Ad Zone</label>
                      <input
                        type="text"
                        placeholder="Ad Zone"
                        style={styles.input}
                        value={store.adZone}
                        onChange={(e) => handleInputChange(index, "adZone", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>ABInBev Wholesaler</label>
                      <input
                        type="text"
                        placeholder="Wholesaler"
                        style={styles.input}
                        value={store.abInBevWholesaler}
                        onChange={(e) =>
                          handleInputChange(index, "abInBevWholesaler", e.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Molson Coors Wholesaler</label>
                      <input
                        type="text"
                        placeholder="Wholesaler"
                        style={styles.input}
                        value={store.millerWholesaler}
                        onChange={(e) =>
                          handleInputChange(index, "millerWholesaler", e.target.value)
                        }
                        // value={store.molsonCoorsWholesaler}
                        // onChange={(e) =>
                        //   handleInputChange(index, "molsonCoorsWholesaler", e.target.value)
                        // }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Constellation Wholesaler</label>
                      <input
                        type="text"
                        placeholder="Wholesaler"
                        style={styles.input}
                        value={store.coorsWholesaler}
                        onChange={(e) =>
                          handleInputChange(index, "coorsWholesaler", e.target.value)
                        }
                        // value={store.constellationWholesaler}
                        // onChange={(e) =>
                        //   handleInputChange(index, "constellationWholesaler", e.target.value)
                        // }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Third Party Wholesaler</label>
                      <input
                        type="text"
                        placeholder="Content"
                        style={styles.input}
                        value={store.thirdPartyWholesaler}
                        onChange={(e) =>
                          handleInputChange(index, "thirdPartyWholesaler", e.target.value)
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Catalyst ID</label>
                      <input
                        type="text"
                        placeholder="-"
                        style={styles.input}
                        value={store.catalystId}
                        onChange={(e) => handleInputChange(index, "catalystId", e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={styles.footer}>
            <button
              style={styles.btnAddAnother}
              onClick={handleAddStore}
              onMouseEnter={() => setAddAnotherHover(true)}
              onMouseLeave={() => setAddAnotherHover(false)}
              disabled={isSubmitting}
            >
              Add Another Store
            </button>
            <button
              style={{
                ...styles.btnSubmit,
                ...(isSubmitting ? { opacity: 0.6, cursor: "not-allowed" } : {}),
              }}
              onClick={handleSubmit}
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Store(s)"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchProject: (id: string | number) => dispatch(fetchProject(id)),
  deleteProject: (id: string | number) => dispatch(deleteProject(id)),
  updateProject: (id: string | number, data: any) => dispatch(updateProject(id, data)),
  // updateNotifications: (data: any) => dispatch(updateNotifications(data)),
  createAnalyticsEvent: (data: any) => dispatch(createAnalyticsEvent(data)),
  uploadFile:
    (prefix: string, uploadUrl: string, options?: { offset?: number; timezone?: string }) =>
    (data: FileUploadState) =>
      dispatch(uploadFile(prefix, uploadUrl, options)(data)),
  resetUpload: (prefix: string) => dispatch(resetUpload(prefix)),
});

const connector = connect(
  (state: State) => ({
    project: state.project.content,
    "Project File Error": state.STORE2POG_file_upload.hasError || false,
    "New Users Error": state.USERS_file_upload.hasError || false,
    "New Stores Error": state.STORES_file_upload.hasError || false,
    "User to Store Mapping Error": state.USER2STORE_file_upload.hasError || false,
  }),
  mapDispatchToProps
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(CreateStoreModal));

const styles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(128, 128, 128, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    //   padding: "20px",
  },
  container: {
    background: "white",
    borderRadius: "16px",
    width: "90%",
    height: "90%",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column" as const,
    padding: "20px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: " rgba(245, 188, 52, 0.11)",
  },
  title: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#000",
    margin: 0,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "16px",
    color: "#6b7280",
    cursor: "pointer",
    padding: 0,
    //   width: "32px",
    //   height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s",
  },
  content: {
    //   padding: "24px 32px",
    padding: "20px 16px",
    flex: 1,
    overflow: "auto",
    //   backgroundColor: "red",
  },
  downloadLink: {
    color: "#f59e0b",
    fontSize: "14px",
    textDecoration: "none",
    fontWeight: 500,
    display: "block",
    textAlign: "right" as const,
    marginBottom: "5px",
  },
  uploadBox: {
    border: "1.5px dashed rgba(146, 148, 151, 1)",
    borderRadius: "12px",
    padding: "0px 20px",
    textAlign: "center" as const,
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "#fafafa",
    //   marginBottom: "32px",
    display: "flex",
    alignItems: "center",
  },
  uploadBoxHover: {
    borderColor: "#f59e0b",
    backgroundColor: "#fffbf0",
  },
  uploadIcon: {
    fontSize: "32px",
    //   marginBottom: "12px",
    opacity: 0.5,
    paddingRight: "15px",
  },
  uploadText: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
  },
  uploadLink: {
    color: "#f59e0b",
    fontWeight: 500,
  },
  sectionTitle: {
    marginBottom: "20px",
    //   marginTop: "32px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#000",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
    //   marginBottom: "24px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column" as const,
  },
  label: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#374151",
    marginBottom: "2px",
  },
  required: {
    color: "#ef4444",
    marginLeft: "2px",
  },
  input: {
    padding: "10px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "16px",
    fontSize: "14px",
    color: "#111827",
    outline: "none",
    transition: "all 0.2s",
  },
  select: {
    padding: "6px 14px",
    border: "1px solid #d1d5db",
    borderRadius: "16px",
    fontSize: "14px",
    color: "#9ca3af",
    outline: "none",
    cursor: "pointer",
    backgroundColor: "white",
    appearance: "none" as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "36px",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px 32px",
    //   borderTop: "1px solid #e5e7eb",
    flexShrink: 0,
  },
  btnAddAnother: {
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "rgba(227, 175, 50, 1)",
    color: "#000",
    border: "none",
    outline: "none",
  },
  btnSubmit: {
    padding: "4px 9px",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "4px",
    border: "none",
    outline: "none",
    cursor: "pointer",
    transition: "all 0.2s",
    backgroundColor: "rgba(227, 175, 50, 1)",
    color: "#000",
  },
};
